/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { Icon, useToast } from '@coreai/component-library';
import { useLanguage } from 'LanguageContext';
import rewrite from '../../../assets/rewrite.svg';
import DeleteRewriteDialog from './DeleteRewriteDialog';
import { deleteScrutinizedWords } from 'services/scrutinizedWordService';
import VBars from 'components/CommonUi/Loaders/VBars';
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useAnalysis } from '../Analyses/FormComponents/AnalysisResultContext';

const styles = {
    rewriteButton: (showRewrite: boolean) => css`
    flex: ${showRewrite ? "1 1 50%" : "1 1 25%"};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    color: #607A8C;
    font-size: 0.75rem;
    padding: 6px 10px;
    border-radius: 6px;
    background: transparent;
    transition: all 0.3s ease;
    
    >img{
      cursor: pointer;
      width: 22px;
      height: 22px;
    }
  `,
    rewriteBox: css`
    position: relative;
    flex: 1 1 50%;
    border-radius: 10px;
    padding: 10px 12px;
    background-color: #F2F6F8;
    font-size: 0.85rem;
    color: #333;
    min-height: 80px;
    min-width: 40%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.3s ease;
  `,

  closeButton: css`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 12px;
    height: 12px;
    background-color: #fff;
    border: none;
    color: #607A8C;
    background-color: #F2F6F8;
    cursor: pointer;
    &:hover {
      color: #000;
    }
  `,

  suggestedValue: css`
    margin-top: 0px;
    font-size: 12px;
    font-weight: 500;
    padding-right: 10px;
    line-height: 20px;
    p {
      margin-top: 0px;
      font-size: 12px;
     }
    strong, b {
      font-size: 12px;
      font-weight: 600;
     }
  `,

  rewriteFooter: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 8px;
  `,

  leftActions: css`
    display: flex;
    gap: 12px;
  `,

  footerButton: css`
    background: transparent;
    border: 1px solid #607A8C;
    color: #607A8C;
    font-size: 0.75rem;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    &:hover {
      background-color: #E8EEF1;
    }
  `,

  rightActions: css`
    display: flex;
    gap: 4px;
  `,

  carouselButton: css`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid #ACB8BD;
    background-color: #fff;
    font-size: 0.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      background-color: #F2F6F8;
    }
  `,
  rewriteText: css`
    margin: 0;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 150%;
    color: #000;
  `,
};

interface RewriteProps {
  showRewrite: boolean;
  rewriteKey: string;
  restGeneratedWordOccurences: () => void;
  handleRewriteClick: (mode: "open" | "close" | "regenerate" | "getAll", payload?: any) => Promise<void>;
  occurence: any;
  generatedWord: any[];
  loading: boolean;
}

const Rewrite = ({ restGeneratedWordOccurences, showRewrite, rewriteKey, handleRewriteClick, generatedWord, loading, occurence }: RewriteProps) => {
    const [showRewriteDialog, setShowRewriteDialog] = useState(false);
    const [generatedItems, setGeneratedItems] = useState<any>([]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [actionLoading, setActionLoading] = useState(false);

    const { t } = useLanguage();
    const theme = useTheme();
    const { toast } = useToast();
    const {analysisContextResult} = useAnalysis();

    useEffect(() => {
      if (Array.isArray(generatedWord) && generatedWord.length > 0) {
        setGeneratedItems(generatedWord);
        setCurrentIndex(generatedWord.length -1);
      }
    }, [generatedWord]);

    const onCloseDialog = () => {
        setShowRewriteDialog(false);
    };

    const onRewriteDialogDelete = async (selectedIds: string[]) => {
      setDeleteLoading(true);
    
      const payload = {
        rephrase_ids: selectedIds,
      };
    
      try {
        const response = await deleteScrutinizedWords(payload);
    
        if (response?.success) {
          setGeneratedItems((prev: any) => {
            const updated = prev.filter((item: any) => !selectedIds.includes(item.id));
    
            if (currentIndex >= updated?.length) {
              if (updated.length > 0) {
                setCurrentIndex(updated.length - 1);  
              } else {
                occurence.is_rewritten = false;
                setCurrentIndex(0);
                restGeneratedWordOccurences();
                handleRewriteClick("close");
              }
            }
    
            return updated;
          });
    
          toast({
            variant: "success",
            title: t("rephrased_delete_toast_confirm", null, analysisContextResult?.locale_code),
          });
        }
      } catch (err: any) {
        toast({
          variant: "error",
          title: err?.response?.data?.message,
        });
      } finally {
        setDeleteLoading(false);
      }
    };    

    const onDeleteModalOpen = (e : React.MouseEvent) => {
        e.stopPropagation();
        setShowRewriteDialog(true);
    };

    const onMoveLeft = () => {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };
    
    const onMoveRight = () => {
      setCurrentIndex((prev) =>
        prev < generatedItems.length - 1 ? prev + 1 : prev
      );
    };

    const handleRegenerateList = async () => {
      setActionLoading(true);
    
      try {
        await handleRewriteClick("regenerate");
      } finally {
        setActionLoading(false);
      }
    };       

    const buttonStyle = () => css`
      color: #000;
      padding: 0px !important;
      width: 12px;
      height: 12px;
      margin-top: -2px;
    `;

    const rewrittenIcon = () => css`
      color: ${theme.colors.primary};
      width: 22px;
      height: 22px;
      background-color: #F2F6F8;
      cursor: pointer;
      border-radius: 50%;
      padding: 4px;
      transform: translateY(-2px);
    `;
    
    const rewrittenText = () => css`
      margin: 0;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 150%;
      color: ${theme.colors.primary};
    `;
    
    const chevronStyle = (disabled: boolean = false) => css`
      color: #000;
      padding: 0px !important;
      width: 12px;
      height: 12px;
      margin-top: 4px;
      cursor: ${disabled ? "default" : "pointer"};
      opacity: ${disabled ? 0.4 : 1};
      transition: opacity 0.2s ease, cursor 0.2s ease;
    `;
    
    const actionButtonWrapper = (actionLoading: boolean) => css`
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 3px;
      cursor:  ${actionLoading ? "default" : "pointer"};
      transition: color 0.2s ease;
    
      > p {
        font-size: 12px;
        font-weight: 500;
        line-height: 150%;
        margin-top: 0;
        color: #000;
        transition: color 0.2s ease;
      }
    
      svg,
      img {
        transition: filter 0.2s ease;
      }
    
      ${!actionLoading &&
        `
          &:hover {
            > p {
              color: ${theme.colors.primary};
            }
      
            svg {
              color: ${theme.colors.primary};
            }
      
            img {
              filter: brightness(0) saturate(100%) invert(37%) sepia(79%)
                saturate(2871%) hue-rotate(202deg) brightness(97%) contrast(96%);
            }
          }
        `
      }
    `;

    return (
        <>
            {showRewrite ? (
                <div css={styles.rewriteBox}>
                  <Icon css={styles.closeButton} name="x-close" size="xs" onClick={() => handleRewriteClick("close")}/>
                  {
                    actionLoading ?
                    <div style={{ paddingTop: "16px" }}>
                      <VBars size="small" />
                    </div>
                    :
                    <p css={styles.suggestedValue}>
                      <Markdown rehypePlugins={[rehypeRaw]}>{generatedItems?.[currentIndex]?.rephrase}</Markdown>
                    </p>                 
                  }
                  <div css={styles.rewriteFooter}>
                    <div css={styles.leftActions}>
                        <div css={actionButtonWrapper(actionLoading)} onClick={!actionLoading ? (e) => onDeleteModalOpen(e) : undefined}>
                            <Icon css={buttonStyle} name="trash" size="xs"/>
                            <p>{t('core_delete', null, analysisContextResult?.locale_code)}</p>
                        </div>
                        <div css={actionButtonWrapper(actionLoading)} onClick={!actionLoading ? handleRegenerateList : undefined}>
                            <Icon css={buttonStyle} name="refresh-right" size="xs"/>
                            <p>{t("rephrased_regenerate", null, analysisContextResult?.locale_code)}</p>
                        </div>
                    </div>
                    {
                      (generatedItems?.length > 1) &&
                      <div css={styles.rightActions}>
                        <Icon
                          css={chevronStyle(currentIndex === 0)}
                          name="chevron-left"
                          size="xs"
                          onClick={onMoveLeft}
                        />
                        <span>{currentIndex + 1}/{generatedItems.length}</span>
                        <Icon
                          css={chevronStyle(currentIndex === generatedItems.length - 1)}
                          name="chevron-right"
                          size="xs"
                          onClick={onMoveRight}
                        />
                      </div>
                    }
                  </div>
                </div>
                ) : occurence?.is_rewritten ? (
                  <div
                  css={styles.rewriteButton(showRewrite)}
                  onClick={() => handleRewriteClick("getAll")}>
                    <Icon name="eye" size="xs" css={rewrittenIcon}/>
                  </div>
                ) : (
                <div css={styles.rewriteButton(showRewrite)} onClick={() => handleRewriteClick("open").then((resValue: any)=> {
                  if(resValue?.success) {
                    occurence.is_rewritten = true 
                  }
                })}>
                    <img src={rewrite} alt="rewrite" />
                </div>
            )}
            {showRewriteDialog && 
              <DeleteRewriteDialog
                  item={generatedItems}
                  open={showRewriteDialog}
                  onClose={onCloseDialog}
                  onDelete={onRewriteDialogDelete}
                  loader={deleteLoading}
              />
            }
        </>
    )
}

export default Rewrite;